import { memo, type MouseEvent, useMemo } from "react";

import { useCartLine } from "@shopify/hydrogen-react";
import {
  type CartLine,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";

import { getLoyaltyTier } from "@tablescape/utilities/customer";

import { CartProductGiftItemSelectionView } from "./CartProductGiftItemSelection.view";

type Props = {
  buttonLabel?: string;
  cartLineUpdating: boolean;
  handleOpenGiftWithPurchaseDrawer: (e: MouseEvent<HTMLElement>) => void;
  handlePreloadProduct: (e: MouseEvent<HTMLElement>) => void;
  linkAriaLabel?: string;
  selectedGiftsWithPurchase: CartLine[] | undefined;
  selectedOptions: Partial<SelectedOption>[] | undefined;
  shippingNote: string | null;
};

const CartProductGiftItemSelectionBase = ({
  buttonLabel,
  cartLineUpdating,
  handleOpenGiftWithPurchaseDrawer,
  handlePreloadProduct,
  linkAriaLabel,
  selectedGiftsWithPurchase,
  selectedOptions,
  shippingNote,
}: Props) => {
  const { merchandise } = useCartLine();

  const hasAvailableGiftsWithPurchase = useMemo(() => {
    const ref = merchandise?.giftWithPurchase?.reference;

    const loyaltyTier = getLoyaltyTier();
    const isCustomerEligibleForGwp =
      !ref?.eligibleLoyaltyTiers?.value ||
      (ref?.eligibleLoyaltyTiers?.value &&
        loyaltyTier &&
        ref.eligibleLoyaltyTiers.value.includes(loyaltyTier));

    return (
      isCustomerEligibleForGwp &&
      !!ref?.products?.references?.nodes?.length &&
      (!ref?.autoAddProducts?.value || ref?.autoAddProducts?.value === "false")
    );
  }, [merchandise?.giftWithPurchase?.reference]);

  if (!hasAvailableGiftsWithPurchase) {
    return null;
  }

  return (
    <CartProductGiftItemSelectionView
      buttonLabel={buttonLabel}
      cartLineUpdating={cartLineUpdating}
      handleOpenGiftWithPurchaseDrawer={handleOpenGiftWithPurchaseDrawer}
      handlePreloadProduct={handlePreloadProduct}
      linkAriaLabel={linkAriaLabel}
      selectedGiftsWithPurchase={selectedGiftsWithPurchase}
      selectedOptions={selectedOptions}
      shippingNote={shippingNote}
    />
  );
};

export const CartProductGiftItemSelection = memo(
  CartProductGiftItemSelectionBase
);
